import { Checkbox, Table, Typography } from 'antd';
import { FC } from 'react';

import type { ContactPersonPlan, ContactPersonValues } from '@hooks/api';

import * as S from './styled';

const { Text } = Typography;

const FIELDS: { key: keyof ContactPersonValues; label: string }[] = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
];

/** What the run will do, in one sentence, before the table shows it field by field. */
const SUMMARY: Record<ContactPersonPlan['action'], string> = {
  overwrite:
    'The owner in Master Data is a different person from the contact person legacy holds, so all four values are replaced.',
  merge:
    "What Master Data holds for the owner is written; anything it has no value for stays as legacy holds it. The contact person's language is never changed.",
  unchanged: 'Legacy already holds what Master Data would write.',
  skipped: '',
};

type Row = { key: string; label: string; current: string | null; proposed: string | null; after: string | null };

const value = (text: string | null) => text ?? <Text type="secondary">—</Text>;

type Props = {
  plan: ContactPersonPlan;
  /** Who the owner is and why — the same sentence the candidate table shows. */
  ownerNote: string | null;
  overwrite: boolean;
  onOverwriteChange: (overwrite: boolean) => void;
};

/**
 * The contact person the action writes, shown before it is pressed.
 *
 * The run replaces what legacy holds — a CSM's hand edit included — with the owner's details from
 * Master Data, and the owner can be a guess ("the earliest-created linked person"). So the section
 * shows both sides and the outcome, and lets the CSM switch the overwrite off for this run.
 *
 * "After this run" follows the switch: unchecked, it is what legacy holds now, which is exactly
 * what will happen.
 */
const ContactPersonSection: FC<Props> = ({ plan, ownerNote, overwrite, onOverwriteChange }) => {
  const writes = plan.action === 'overwrite' || plan.action === 'merge';
  const after = writes && overwrite ? plan.result : plan.current;

  const rows: Row[] = FIELDS.map(({ key, label }) => ({
    key,
    label,
    current: plan.current[key],
    proposed: plan.proposed[key],
    after: after[key],
  }));

  return (
    <div>
      <S.SectionLabel>Contact person</S.SectionLabel>
      <S.SectionNote type="secondary">
        {plan.action === 'skipped'
          ? plan.reason
          : writes && !overwrite
            ? 'The overwrite is switched off for this run, so the contact person stays as legacy holds it.'
            : SUMMARY[plan.action]}
        {ownerNote && <> Owner: {ownerNote}.</>}
      </S.SectionNote>
      <Table<Row>
        size="small"
        rowKey="key"
        pagination={false}
        dataSource={rows}
        columns={[
          { title: '', key: 'label', dataIndex: 'label', width: 110 },
          { title: 'In legacy now', key: 'current', render: (_: unknown, row) => value(row.current) },
          { title: 'From Master Data', key: 'proposed', render: (_: unknown, row) => value(row.proposed) },
          {
            title: 'After this run',
            key: 'after',
            // Bold where the run changes something, so the diff reads without comparing columns.
            render: (_: unknown, row) =>
              row.after !== row.current ? <Text strong>{row.after || '—'}</Text> : value(row.after),
          },
        ]}
      />
      {/*
       * Offered whatever the preview says: the backend recomputes the plan when the button is
       * pressed, so a "skipped" or "unchanged" here can still be a write by then — and the CSM must be
       * able to refuse that too.
       */}
      <Checkbox checked={overwrite} onChange={(event) => onOverwriteChange(event.target.checked)}>
        Overwrite the contact person from Master Data
      </Checkbox>
    </div>
  );
};

export default ContactPersonSection;
